/**
 * <sk-slider> — Universal slider/marquee custom element.
 *
 * Data attributes (set in Liquid):
 *   data-slides-desktop  (float) slides visible on >=990px   default 3
 *   data-slides-tablet   (float) slides visible on >=750px   default 2
 *   data-slides-mobile   (float) slides visible on <750px    default 1
 *   data-scroll-count    (int)   slides to scroll per click  default 1
 *   data-gap             (px)    gap between slides           default 16
 *   data-loop            (bool)  seamless infinite loop (clones slides)
 *   data-autoplay        ("step"|"continuous"|"marquee"|false)
 *   data-autoplay-speed  (ms)    interval for step autoplay   default 4000
 *   data-marquee-speed   (px/s)  speed for marquee/continuous  default 40
 *   data-marquee-dir     ("ltr"|"rtl")                        default "ltr"
 */
class SkSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-sk-track]');
    this.slides = [];
    this.prevBtn = this.querySelector('[data-sk-prev]');
    this.nextBtn = this.querySelector('[data-sk-next]');
    this.dotsWrap = this.querySelector('[data-sk-dots]');
    this.counterCurrent = this.querySelector('[data-sk-counter-current]');
    this.counterTotal = this.querySelector('[data-sk-counter-total]');

    if (!this.track) return;

    this._autoplayMode = this.dataset.autoplay || '';
    this._isMarquee = this._autoplayMode === 'marquee';
    this._rafId = null;
    this._paused = false;

    this._init();
  }

  _init() {
    this.slides = Array.from(this.track.children);
    if (!this.slides.length) return;

    if (this._isMarquee) {
      this._initMarquee();
    } else {
      this._initSlider();
    }
  }

  /* ═══════════════════════════════════════════════════
     Slider mode
     ═══════════════════════════════════════════════════ */

  _initSlider() {
    this._page = 0;
    this._originalSlideCount = this.slides.length;
    this._isContinuous = this._autoplayMode === 'continuous';
    this._seamlessLoop = this.hasAttribute('data-loop') && this._originalSlideCount > 0;
    this._teleporting = false;

    if (this._seamlessLoop) {
      const html = this.track.innerHTML;
      this.track.insertAdjacentHTML('afterbegin', html);
      this.track.insertAdjacentHTML('beforeend', html);
      this.slides = Array.from(this.track.children);
      this._needsInitialScroll = true;
    }

    if (this._isContinuous) {
      this.track.style.scrollSnapType = 'none';
    }

    this._resizeObs = new ResizeObserver(() => this._updateSlider());
    this._resizeObs.observe(this.track);

    this.track.addEventListener('scroll', () => this._onScroll(), { passive: true });
    if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.preventDefault(); this._go(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.preventDefault(); this._go(1); });

    this._initPointerDrag();

    if (this._isContinuous) this._initContinuousAutoplay();
    else if (this._autoplayMode === 'step') this._initStepAutoplay();

    this._updateSlider();
  }

  // Supports fractional values (e.g. 2.5 shows 2 and a half slides)
  get _slidesPerView() {
    const w = window.innerWidth;
    if (w >= 990) return parseFloat(this.dataset.slidesDesktop) || 3;
    if (w >= 750) return parseFloat(this.dataset.slidesTablet) || 2;
    return parseFloat(this.dataset.slidesMobile) || 1;
  }

  get _scrollCount() {
    return parseInt(this.dataset.scrollCount) || 1;
  }

  get _gap() {
    return parseInt(this.dataset.gap) || 16;
  }

  get _totalPages() {
    const count = this._originalSlideCount || this.slides.length;
    return Math.max(1, Math.ceil((count - Math.floor(this._slidesPerView)) / this._scrollCount) + 1);
  }

  _slideWidth() {
    if (!this.slides.length) return 0;
    return this.slides[0].offsetWidth + this._gap;
  }

  get _loopOffset() {
    return this._seamlessLoop ? this._originalSlideCount * this._slideWidth() : 0;
  }

  /* ── Navigation ── */

  _go(dir) {
    const slideW = this._slideWidth();
    if (!slideW) return;
    let target = this.track.scrollLeft + this._scrollCount * slideW * dir;

    if (!this._seamlessLoop) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      target = Math.max(0, Math.min(target, max));
    }

    this.track.scrollTo({ left: target, behavior: 'smooth' });
  }

  /* ── Pointer drag-to-scroll (mouse + touch) ── */

  _initPointerDrag() {
    const track = this.track;
    const THRESHOLD = 10;
    let down = false;
    let moved = false;
    let isTouch = false;
    let axis = null;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true;
      moved = false;
      axis = null;
      isTouch = e.pointerType !== 'mouse';
      startX = e.clientX;
      startY = e.clientY;
      startScroll = track.scrollLeft;
      pointerId = e.pointerId;
    });

    track.addEventListener('pointermove', (e) => {
      if (!down || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      if (!moved) {
        if (adx < THRESHOLD && ady < THRESHOLD) return;
        if (isTouch && ady > adx) {
          down = false;
          axis = null;
          try { track.releasePointerCapture(pointerId); } catch (err) {}
          pointerId = null;
          return;
        }
        moved = true;
        axis = 'x';
        track.classList.add('sk-slider__track--grabbing');
        this._dragPrevSnap = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        this._paused = true;
        try { track.setPointerCapture(pointerId); } catch (err) {}
      }

      if (axis !== 'x') return;
      track.scrollLeft = startScroll - dx;
      e.preventDefault();
    }, { passive: false });

    const end = (e) => {
      if (!down && !moved) return;
      const wasDrag = moved;
      down = false;
      moved = false;
      axis = null;
      if (wasDrag) {
        track.classList.remove('sk-slider__track--grabbing');
        track.style.scrollSnapType = this._dragPrevSnap || '';
        this._paused = false;
        this._suppressClick = true;
        setTimeout(() => { this._suppressClick = false; }, 300);
        if (this._seamlessLoop && !this._isContinuous) {
          this._teleportIfNeeded();
        }
      }
      if (pointerId != null) {
        try { track.releasePointerCapture(pointerId); } catch (err) {}
      }
      pointerId = null;
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);

    track.addEventListener('click', (e) => {
      if (this._suppressClick) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    track.addEventListener('dragstart', (e) => {
      if (e.target && e.target.tagName === 'IMG') e.preventDefault();
    });
  }

  /* ── Scroll tracking & seamless teleport ── */

  _onScroll() {
    if (this._teleporting) return;

    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    this._scrollRaf = requestAnimationFrame(() => {
      const slideW = this._slideWidth();
      if (!slideW) return;
      const adjusted = this.track.scrollLeft - this._loopOffset;
      const raw = Math.round(adjusted / (slideW * this._scrollCount));
      const total = this._totalPages;
      const bounded = ((raw % total) + total) % total;
      if (bounded !== this._page) {
        this._page = bounded;
        this._updateControls();
      }
    });

    if (this._seamlessLoop && !this._isContinuous) {
      clearTimeout(this._teleportTimer);
      this._teleportTimer = setTimeout(() => this._teleportIfNeeded(), 120);
    }
  }

  _teleportIfNeeded() {
    const slideW = this._slideWidth();
    if (!slideW) return;
    const origW = this._originalSlideCount * slideW;
    let delta = 0;

    if (this.track.scrollLeft >= origW * 2) delta = -origW;
    else if (this.track.scrollLeft < origW) delta = origW;

    if (delta) {
      this._teleporting = true;
      const snap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft += delta;
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = snap || '';
        this.track.style.scrollBehavior = '';
        this._teleporting = false;
      });
    }
  }

  /* ── Controls / dots / counter ── */

  _updateSlider() {
    if (this._needsInitialScroll) {
      this._needsInitialScroll = false;
      const slideW = this._slideWidth();
      if (slideW > 0) {
        this._teleporting = true;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this._originalSlideCount * slideW;
        this.track.style.scrollBehavior = '';
        this._teleporting = false;
      }
    }
    this._updateControls();
    this._buildDots();
  }

  _updateControls() {
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
    const count = this._originalSlideCount || this.slides.length;
    const allVisible = count <= Math.floor(this._slidesPerView);

    if (this.prevBtn) {
      this.prevBtn.disabled = !this._seamlessLoop && atStart;
      this.prevBtn.style.display = allVisible ? 'none' : '';
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = !this._seamlessLoop && atEnd;
      this.nextBtn.style.display = allVisible ? 'none' : '';
    }

    if (this.counterCurrent) this.counterCurrent.textContent = this._page + 1;
    if (this.counterTotal) this.counterTotal.textContent = this._totalPages;

    if (this.dotsWrap) {
      const dots = this.dotsWrap.querySelectorAll('[data-sk-dot]');
      dots.forEach((d, i) => d.classList.toggle('sk-slider__dot--active', i === this._page));
    }
  }

  _buildDots() {
    if (!this.dotsWrap) return;
    const total = this._totalPages;
    if (total <= 1) { this.dotsWrap.innerHTML = ''; return; }

    const frag = document.createDocumentFragment();
    for (let i = 0; i < total; i++) {
      const btn = document.createElement('button');
      btn.setAttribute('data-sk-dot', '');
      btn.className = 'sk-slider__dot' + (i === this._page ? ' sk-slider__dot--active' : '');
      btn.setAttribute('aria-label', `Slide ${i + 1}`);
      btn.addEventListener('click', () => {
        const slideW = this._slideWidth();
        this.track.scrollTo({
          left: this._loopOffset + i * this._scrollCount * slideW,
          behavior: 'smooth'
        });
      });
      frag.appendChild(btn);
    }
    this.dotsWrap.innerHTML = '';
    this.dotsWrap.appendChild(frag);
  }

  /* ── Step autoplay ── */

  _initStepAutoplay() {
    const speed = parseInt(this.dataset.autoplaySpeed) || 4000;
    this._stepInterval = null;

    const start = () => {
      if (this._stepInterval) return;
      this._stepInterval = setInterval(() => this._go(1), speed);
    };
    const stop = () => { clearInterval(this._stepInterval); this._stepInterval = null; };

    this.addEventListener('mouseenter', stop);
    this.addEventListener('mouseleave', start);
    this.addEventListener('focusin', stop);
    this.addEventListener('focusout', start);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    start();
  }

  /* ── Continuous autoplay (smooth RAF-based scroll) ── */

  _initContinuousAutoplay() {
    this._continuousSpeed = parseFloat(this.dataset.marqueeSpeed) || 40;
    this._continuousDir = (this.dataset.marqueeDir || 'ltr') === 'rtl' ? -1 : 1;
    this._scrollAccum = 0;

    this.addEventListener('mouseenter', () => { this._paused = true; });
    this.addEventListener('mouseleave', () => { this._paused = false; });
    this.addEventListener('focusin', () => { this._paused = true; });
    this.addEventListener('focusout', () => { this._paused = false; });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._lastTs = 0;
    this._rafId = requestAnimationFrame((ts) => this._tickContinuous(ts));
  }

  _tickContinuous(ts) {
    if (this._lastTs) {
      const dt = (ts - this._lastTs) / 1000;
      if (!this._paused) {
        // Accumulate sub-pixel values to avoid rounding issues at low speeds
        this._scrollAccum += this._continuousSpeed * dt * this._continuousDir;
        const delta = Math.trunc(this._scrollAccum);
        if (delta !== 0) {
          this.track.scrollLeft += delta;
          this._scrollAccum -= delta;
        }

        if (this._seamlessLoop) {
          const origW = this._originalSlideCount * this._slideWidth();
          if (origW > 0) {
            if (this.track.scrollLeft >= origW * 2) this.track.scrollLeft -= origW;
            else if (this.track.scrollLeft < origW) this.track.scrollLeft += origW;
          }
        }
      }
    }
    this._lastTs = ts;
    this._rafId = requestAnimationFrame((t) => this._tickContinuous(t));
  }

  /* ═══════════════════════════════════════════════════
     Marquee mode (transform-based, purely visual)
     ═══════════════════════════════════════════════════ */

  _initMarquee() {
    this.track.classList.add('sk-slider__track--marquee');

    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.dotsWrap) this.dotsWrap.style.display = 'none';

    this._marqueeSpeed = parseFloat(this.dataset.marqueeSpeed) || 40;
    this._marqueeDir = (this.dataset.marqueeDir || 'ltr') === 'rtl' ? -1 : 1;

    // Clone strategy depends on direction
    const clone = this.track.innerHTML;
    if (this._marqueeDir === -1) {
      this.track.insertAdjacentHTML('afterbegin', clone);
    } else {
      this.track.insertAdjacentHTML('beforeend', clone);
    }

    this._marqueeOffset = 0;
    this._contentWidth = 0;

    this.addEventListener('mouseenter', () => { this._paused = true; });
    this.addEventListener('mouseleave', () => { this._paused = false; });
    this.addEventListener('focusin', () => { this._paused = true; });
    this.addEventListener('focusout', () => { this._paused = false; });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._measureMarquee();
    this._resizeObs = new ResizeObserver(() => this._measureMarquee());
    this._resizeObs.observe(this.track);
    this._lastTs = 0;
    this._rafId = requestAnimationFrame((ts) => this._tickMarquee(ts));
  }

  _measureMarquee() {
    const children = Array.from(this.track.children);
    const half = children.length / 2;
    let w = 0;
    for (let i = 0; i < half; i++) w += children[i].offsetWidth;
    this._contentWidth = w + this._gap * half;

    // RTL: start offset at contentWidth so originals are visible
    if (this._marqueeDir === -1 && this._marqueeOffset === 0) {
      this._marqueeOffset = this._contentWidth;
    }
  }

  _tickMarquee(ts) {
    if (this._lastTs) {
      const dt = (ts - this._lastTs) / 1000;
      if (!this._paused && this._contentWidth > 0) {
        this._marqueeOffset += this._marqueeSpeed * dt * this._marqueeDir;

        // LTR: offset 0 → contentWidth, reset to 0
        if (this._marqueeDir === 1 && this._marqueeOffset >= this._contentWidth) {
          this._marqueeOffset -= this._contentWidth;
        }
        // RTL: offset contentWidth → 0, reset to contentWidth
        else if (this._marqueeDir === -1 && this._marqueeOffset <= 0) {
          this._marqueeOffset += this._contentWidth;
        }

        this.track.style.transform = `translateX(${-this._marqueeOffset}px)`;
      }
    }
    this._lastTs = ts;
    this._rafId = requestAnimationFrame((t) => this._tickMarquee(t));
  }

  /* ── Cleanup ── */

  disconnectedCallback() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._stepInterval) clearInterval(this._stepInterval);
    if (this._resizeObs) this._resizeObs.disconnect();
  }
}

customElements.define('sk-slider', SkSlider);
